import { storeToRefs } from 'pinia';
import { computed, type ComputedRef } from 'vue';
import { useToastStore } from '@/stores/toast.store';
import type { ToastOptions, ToastType, ToastItem } from '@/types/toastTypes';

export interface UseToastApi {
  allActiveToasts: ComputedRef<ToastItem[]>;
  show: (options: ToastOptions) => string;
  success: (title: string, opts?: Omit<ToastOptions, 'title' | 'type'>) => string;
  error: (title: string, opts?: Omit<ToastOptions, 'title' | 'type'>) => string;
  info: (title: string, opts?: Omit<ToastOptions, 'title' | 'type'>) => string;
  warning: (title: string, opts?: Omit<ToastOptions, 'title' | 'type'>) => string;
  neutral: (title: string, opts?: Omit<ToastOptions, 'title' | 'type'>) => string;
  queueForNextLoad: (options: ToastOptions) => string;
  consumeQueuedForNextLoad: () => ToastItem[];
  dismiss: (id: string) => void;
  clearImmediate: () => void;
  clearImmediateOnNavigation: () => void;
  clearAll: () => void;
}

export default function useToast(): UseToastApi {
  const toastStore = useToastStore();
  const { immediateToasts } = storeToRefs(toastStore);

  const allActiveToasts = computed(() => immediateToasts.value);

  const show = (options: ToastOptions) => toastStore.show(options);

  const makeSimple =
    (type: ToastType) =>
    (title: string, opts: Omit<ToastOptions, 'title' | 'type'> = {}) =>
      toastStore.show({
        ...opts,
        title,
        type,
      });

  const success = makeSimple('success');
  const error = makeSimple('error');
  const info = makeSimple('info');
  const warning = makeSimple('warning');
  const neutral = makeSimple('neutral');

  const queueForNextLoad = (options: ToastOptions) => toastStore.queueForNextLoad(options);

  const consumeQueuedForNextLoad = () => toastStore.consumeQueuedForNextLoad();

  const dismiss = (id: string) => toastStore.removeById(id);

  const clearImmediate = () => toastStore.clearImmediate();

  const clearImmediateOnNavigation = () => toastStore.clearImmediateOnNavigation();

  const clearAll = () => toastStore.clearAll();

  return {
    allActiveToasts,
    show,
    success,
    error,
    info,
    warning,
    neutral,
    queueForNextLoad,
    consumeQueuedForNextLoad,
    dismiss,
    clearImmediate,
    clearImmediateOnNavigation,
    clearAll,
  };
}

